import { use } from 'react';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Alert from 'shared/components/alert/alert';
import BreadCrumb from 'shared/components/bread-crumb/bread-crumb/bread-crumb';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import { PersonalNumberRange } from 'features/care-connect-settings/domain/models/personal-number-range';
import PersonalNumberRangeRowProvider from 'features/care-connect-settings/providers/personal-number-range-row-provider';
import PersonalNumberRangeRow from 'features/care-connect-settings/views/components/personal-number-range-row/personal-number-range-row';
import styles from './care-connect-personal-number-range-details.module.scss';

const CareConnectPersonalNumberRangeDetails = (): React.JSX.Element => {
  const { t } = useTranslation('careConnectSettings');

  const { selectedOrganisationUnit, selectedOrganisationUnitBreadcrumbs, personalNumberRangeDetails } =
    use(CareConnectSettingsProviderContext);
  const { personalNumberRanges, addPersonalNumberRange, isEditing } = personalNumberRangeDetails;

  const renderPersonalNumberRangeListItem = (personalNumberRange: PersonalNumberRange): React.JSX.Element => {
    const viewingMode: ViewingMode = personalNumberRange.id ? 'viewing' : 'creation';
    return (
      <PersonalNumberRangeRowProvider
        key={personalNumberRange.key ?? personalNumberRange.id}
        personalNumberRange={personalNumberRange}
        viewingMode={viewingMode}
      >
        <PersonalNumberRangeRow />
      </PersonalNumberRangeRowProvider>
    );
  };

  const renderNoPersonalNumberRangesMessage = (): React.JSX.Element =>
    !personalNumberRanges?.length ? (
      <Typography className={styles.noPersonalNumberRangesMessage} variant="body2">
        {t('personalNumberRangeDetails.noPersonalNumberRangesMessage')}
      </Typography>
    ) : (
      <></>
    );

  const renderAddPersonalNumberRangeButton = (): React.JSX.Element =>
    !isEditing ? (
      <Button variant="text" onClick={addPersonalNumberRange} sx={{ alignSelf: 'flex-end' }}>
        <AddCircleOutlineIcon sx={{ marginRight: 1 }} fontSize="small" />
        <Typography variant="body1" color="primary">
          {t('personalNumberRangeDetails.addPersonalNumberRangeButton')}
        </Typography>
      </Button>
    ) : (
      <></>
    );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Typography variant="h3">{selectedOrganisationUnit?.name}</Typography>
          {selectedOrganisationUnitBreadcrumbs.length > 0 && (
            <BreadCrumb showEllipsis={selectedOrganisationUnitBreadcrumbs.length > 4} allElementNames={selectedOrganisationUnitBreadcrumbs}>
              {selectedOrganisationUnitBreadcrumbs.map((name) => (
                <Typography key={`breadcrumb-${name}`} variant="body1">
                  {name}
                </Typography>
              ))}
            </BreadCrumb>
          )}
        </div>
      </div>
      <div className={styles.content}>
        <Alert
          title={t('personalNumberRangeDetails.explanationTitle')}
          body={t('personalNumberRangeDetails.explanationBody')}
          severity="info"
        />
        <div className={styles.columnHeaderGap}>
          <Typography variant="h5">{t('personalNumberRangeDetails.personalNumberRangesTitle')}</Typography>
          {renderNoPersonalNumberRangesMessage()}
          {personalNumberRanges?.some((personalNumberRange) => personalNumberRange.id) && (
            <div className={styles.fromToContainer}>
              <div className={styles.columnHeaders}>
                <Typography variant="body1">{t('personalNumberRangeDetails.lowerBoundLabel')}</Typography>
                <Typography variant="body1">{t('personalNumberRangeDetails.upperBoundLabel')}</Typography>
              </div>
              <div className={styles.columnHeaderDivider} />
            </div>
          )}
          <div className={styles.list}>
            {personalNumberRanges?.map((personalNumberRange) => renderPersonalNumberRangeListItem(personalNumberRange))}
          </div>
        </div>
        {renderAddPersonalNumberRangeButton()}
      </div>
    </div>
  );
};

export default CareConnectPersonalNumberRangeDetails;
